import { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { CompletenessGateMode } from '@models/organization.model';

// Sprint 12, story-052 (U72). A5/UX-A7 — lowest-confidence assumption in this sprint: exact
// copy/placement unspecified by the card, following `atavEnabled`'s nearest precedent.
const GATE_MODE_OPTIONS: SelectOption[] = [
  { value: CompletenessGateMode.WARN, label: 'Warn (default)' },
  { value: CompletenessGateMode.BLOCK, label: 'Block' },
];

const GATE_MODE_HELPER_TEXT: Record<CompletenessGateMode, string> = {
  [CompletenessGateMode.WARN]:
    "Field techs can complete a task past a missing required photo, with the gap recorded on the task's activity.",
  [CompletenessGateMode.BLOCK]:
    'Techs must resolve every required item before a task can be completed.',
};

interface CreateEditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  createOrganization?: (
    name: string,
    ownerEmail: string,
    ownerFirstName: string,
    ownerLastName: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
  ) => void;
  editOrganization?: (
    name: string,
    organizationId: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
    completenessGateMode: CompletenessGateMode,
  ) => void;
  saving: boolean;
  editing?: boolean;
  initialData?: {
    name: string;
    organizationId: string;
    showAsSubcontractor: boolean;
    atavEnabled?: boolean;
    completenessGateMode?: CompletenessGateMode;
  } | null;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Organization name is required'),
  ownerEmail: Yup.string().when('isCreateMode', {
    is: true,
    then: (schema) =>
      schema.email('Invalid email').required('Owner email is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  ownerFirstName: Yup.string().when('isCreateMode', {
    is: true,
    then: (schema) => schema.required('Owner first name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  ownerLastName: Yup.string().when('isCreateMode', {
    is: true,
    then: (schema) => schema.required('Owner last name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const CreateEditOrganizationModal: FC<CreateEditOrganizationModalProps> = ({
  isOpen,
  onClose,
  createOrganization,
  editOrganization,
  saving,
  editing = false,
  initialData,
}) => {
  const formik = useFormik({
    initialValues: {
      name: initialData?.name || '',
      ownerEmail: '',
      ownerFirstName: '',
      ownerLastName: '',
      showAsSubcontractor: initialData?.showAsSubcontractor ?? true,
      atavEnabled: initialData?.atavEnabled ?? false,
      completenessGateMode: initialData?.completenessGateMode ?? CompletenessGateMode.WARN,
      isCreateMode: !editing,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (editing && initialData) {
        editOrganization &&
          editOrganization(
            values.name,
            initialData.organizationId,
            values.showAsSubcontractor,
            values.atavEnabled,
            values.completenessGateMode,
          );
      } else {
        createOrganization &&
          createOrganization(
            values.name,
            values.ownerEmail,
            values.ownerFirstName,
            values.ownerLastName,
            values.showAsSubcontractor,
            values.atavEnabled,
          );
      }
    },
  });

  const {
    values,
    handleChange,
    submitForm,
    setFieldValue,
    errors,
    touched,
    resetForm,
  } = formik;

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={saving}
      handleConfirm={submitForm}
      title={editing ? 'Edit Organization' : 'Add Organization'}
      confirmText={editing ? 'Update' : 'Create'}>
      <form className="flex flex-col gap-6">
        <FormGroup
          error={errors.name || undefined}
          htmlFor="name"
          label="Organization Name">
          <Input
            name="name"
            value={values.name}
            onChange={handleChange}
            placeholder={'Organization Name'}
            error={!!(touched.name && errors.name)}
          />
        </FormGroup>

        {!editing && (
          <>
            <div className="flex gap-4">
              <FormGroup
                error={errors.ownerFirstName || undefined}
                htmlFor="ownerFirstName"
                label="Owner First Name">
                <Input
                  name="ownerFirstName"
                  value={values.ownerFirstName}
                  onChange={handleChange}
                  placeholder={'Owner First Name'}
                  error={!!(touched.ownerFirstName && errors.ownerFirstName)}
                />
              </FormGroup>
              <FormGroup
                error={errors.ownerLastName || undefined}
                htmlFor="ownerLastName"
                label="Owner Last Name">
                <Input
                  name="ownerLastName"
                  value={values.ownerLastName}
                  onChange={handleChange}
                  placeholder={'Owner Last Name'}
                  error={!!(touched.ownerLastName && errors.ownerLastName)}
                />
              </FormGroup>
            </div>
            <FormGroup
              error={errors.ownerEmail || undefined}
              htmlFor="ownerEmail"
              label="Owner Email">
              <Input
                name="ownerEmail"
                type="email"
                value={values.ownerEmail}
                onChange={handleChange}
                placeholder={'Owner Email'}
                error={!!(touched.ownerEmail && errors.ownerEmail)}
              />
            </FormGroup>
          </>
        )}

        <FormGroup htmlFor="showAsSubcontractor" label="Show as Subcontractor">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showAsSubcontractor"
              name="showAsSubcontractor"
              checked={values.showAsSubcontractor}
              onChange={(e) =>
                setFieldValue('showAsSubcontractor', e.target.checked)
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor="showAsSubcontractor"
              className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">
              Allow this organization to be shown as a subcontractor
            </label>
          </div>
          <div className="flex items-center mt-2">
            <input
              type="checkbox"
              id="atavEnabled"
              name="atavEnabled"
              checked={values.atavEnabled}
              onChange={(e) => setFieldValue('atavEnabled', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor="atavEnabled"
              className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">
              Enable ATAV field capture (Report Work + Field Capture section)
            </label>
          </div>
        </FormGroup>

        {editing && (
          <FormGroup htmlFor="completenessGateMode" label="Completeness gate mode">
            <ReactSelect
              inputId="completenessGateMode"
              value={GATE_MODE_OPTIONS.find((opt) => opt.value === values.completenessGateMode) || null}
              onChange={(option) => {
                const selected = option as SelectOption | null;
                setFieldValue(
                  'completenessGateMode',
                  (selected?.value as CompletenessGateMode) || CompletenessGateMode.WARN,
                );
              }}
              options={GATE_MODE_OPTIONS}
              isClearable={false}
              isSearchable={false}
            />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {GATE_MODE_HELPER_TEXT[values.completenessGateMode]}
            </p>
          </FormGroup>
        )}
      </form>
    </Modal>
  );
};

export default CreateEditOrganizationModal;
